import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { FiPlus, FiSearch } from "react-icons/fi";
import {
  createRoleRequest,
  deleteRoleRequest,
  getRoleRequest,
  listRolesRequest,
  updateRoleRequest,
} from "../../services/rolesApi";
import { getAllPermissionsRequest } from "../../services/permissionsApi";
import Can from "../../components/auth/Can";
import TableRowActions from "../../components/common/TableRowActions";
import { PERMISSIONS } from "../../constants/permissions";
import { usePermissions } from "../../hooks/usePermissions";

function RolesAccessPage() {
  const { modulePermissions } = usePermissions();
  const perms = modulePermissions("role");
  const showActions = perms.canRead || perms.canUpdate || perms.canDelete;
  const tableColCount = showActions ? 7 : 6;

  const [search, setSearch] = useState("");
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPermissionsLoading, setIsPermissionsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [viewingRole, setViewingRole] = useState(null);
  const [deleteRoleId, setDeleteRoleId] = useState(null);
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const perPage = 10;
  const [form, setForm] = useState({ name: "", slug: "", description: "" });
  const [allPermissions, setAllPermissions] = useState([]);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);

  const normalizeRole = (role) => ({
    id: role?.id,
    name: role?.name || "",
    slug: role?.slug || "",
    description: role?.description || "",
    permissions: role?.permissions || role?.data?.permissions || [],
    created_at: (role?.created_at || role?.createdAt || "-").slice(0, 10),
    updated_at: (role?.updated_at || role?.updatedAt || "-").slice(0, 10),
  });

  const normalizePermission = (permission) => ({
    id: permission?.id,
    name: permission?.name || "",
    slug: permission?.slug || "",
    group:
      permission?.group ??
      permission?.permission_group ??
      permission?.module ??
      "",
  });

  const getRolesFromResponse = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.roles)) return payload.roles;
    if (Array.isArray(payload?.data?.roles)) return payload.data.roles;
    return [];
  };

  const getPermissionsFromResponse = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.permissions)) return payload.permissions;
    if (Array.isArray(payload?.data?.permissions))
      return payload.data.permissions;
    return [];
  };

  const formatGroupLabel = (groupKey) => {
    const key = String(groupKey || "").trim().toLowerCase();
    if (!key) return "Other";
    return key
      .split(/[_-]/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const slugCrudSortOrder = (slug) => {
    const s = String(slug || "").toLowerCase();
    if (s.startsWith("create_") || s.includes("create")) return 1;
    if (s.startsWith("read_") || s.includes("read")) return 2;
    if (s.startsWith("update_") || s.includes("update") || s.includes("edit"))
      return 3;
    if (s.startsWith("delete_") || s.includes("delete")) return 4;
    return 99;
  };

  const groupedPermissions = useMemo(() => {
    const groupsMap = new Map();

    allPermissions.forEach((permission) => {
      const rawGroup = String(permission.group || "").trim().toLowerCase();
      const groupKey = rawGroup || "other";

      if (!groupsMap.has(groupKey)) {
        groupsMap.set(groupKey, {
          key: groupKey,
          label: formatGroupLabel(groupKey === "other" ? "" : groupKey),
          permissions: [],
        });
      }

      groupsMap.get(groupKey).permissions.push(permission);
    });

    if (groupsMap.has("other")) {
      const other = groupsMap.get("other");
      other.label = "Other";
    }

    return Array.from(groupsMap.values())
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((group) => ({
        ...group,
        permissions: [...group.permissions].sort((a, b) => {
          const oa = slugCrudSortOrder(a.slug);
          const ob = slugCrudSortOrder(b.slug);
          if (oa !== ob) return oa - ob;
          return (a.name || "").localeCompare(b.name || "");
        }),
      }));
  }, [allPermissions]);

  useEffect(() => {
    const loadRoles = async () => {
      try {
        setIsLoading(true);
        const response = await listRolesRequest({
          search: search.trim() || undefined,
          page,
          per_page: perPage,
        });
        const payload = response.data;
        const normalizedRoles =
          getRolesFromResponse(payload).map(normalizeRole);
        setRoles(normalizedRoles);

        const fallbackTotal = normalizedRoles.length;
        const resolvedTotal =
          payload?.total ??
          payload?.meta?.total ??
          payload?.data?.total ??
          payload?.data?.meta?.total ??
          fallbackTotal;
        const resolvedLastPage =
          payload?.last_page ??
          payload?.meta?.last_page ??
          payload?.data?.last_page ??
          payload?.data?.meta?.last_page ??
          Math.max(1, Math.ceil(resolvedTotal / perPage));

        setTotalRecords(Number(resolvedTotal) || 0);
        setTotalPages(Number(resolvedLastPage) || 1);
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load roles.");
      } finally {
        setIsLoading(false);
      }
    };

    loadRoles();
  }, [page, search]);

  useEffect(() => {
    const loadPermissions = async () => {
      try {
        setIsPermissionsLoading(true);
        const response = await getAllPermissionsRequest();
        const permissions = getPermissionsFromResponse(response.data).map(
          normalizePermission,
        );
        setAllPermissions(permissions);
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to load permissions.",
        );
      } finally {
        setIsPermissionsLoading(false);
      }
    };

    loadPermissions();
  }, []);

  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const resetModalState = () => {
    setForm({ name: "", slug: "", description: "" });
    setSelectedPermissionIds([]);
    setEditingRoleId(null);
    setIsModalOpen(false);
  };

  const openCreateModal = () => {
    setForm({ name: "", slug: "", description: "" });
    setSelectedPermissionIds([]);
    setEditingRoleId(null);
    setIsModalOpen(true);
  };

  const openEditModal = async (role) => {
    try {
      setEditingRoleId(role.id);
      setIsModalOpen(true);
      const response = await getRoleRequest(role.id);
      const roleDetails = normalizeRole(response.data?.data || response.data);
      setForm({
        name: roleDetails.name,
        slug: roleDetails.slug,
        description: roleDetails.description,
      });
      const ids = (roleDetails.permissions || [])
        .map((permission) => Number(permission.id))
        .filter(Boolean);
      setSelectedPermissionIds(ids);
    } catch (error) {
      setIsModalOpen(false);
      setEditingRoleId(null);
      toast.error(
        error.response?.data?.message || "Failed to load role details.",
      );
    }
  };

  const openViewModal = async (role) => {
    try {
      const response = await getRoleRequest(role.id);
      const roleDetails = normalizeRole(response.data?.data || response.data);
      setViewingRole(roleDetails);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to load role details.",
      );
      setViewingRole(role);
    }
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const togglePermission = (permissionId) => {
    setSelectedPermissionIds((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId],
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const payload = {
      name: form.name.trim(),
      slug: form.slug.trim(),
      description: form.description.trim(),
      permission_ids: selectedPermissionIds,
    };
    if (!payload.name || !payload.slug || !payload.description) return;

    try {
      setIsSubmitting(true);
      if (editingRoleId) {
        const response = await updateRoleRequest(editingRoleId, payload);
        const updatedRole = normalizeRole(response.data?.data || response.data);
        setRoles((prev) =>
          prev.map((role) =>
            role.id === editingRoleId ? { ...role, ...updatedRole } : role,
          ),
        );
        toast.success("Role updated successfully.");
      } else {
        const response = await createRoleRequest(payload);
        const createdRole = normalizeRole(response.data?.data || response.data);
        setRoles((prev) => [createdRole, ...prev]);
        setPage(1);
        toast.success("Role created successfully.");
      }
      resetModalState();
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          (editingRoleId ? "Failed to update role." : "Failed to create role."),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const askDeleteRole = (roleId) => {
    setDeleteRoleId(roleId);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteRoleId) return;
    try {
      await deleteRoleRequest(deleteRoleId);
      setRoles((prev) => prev.filter((role) => role.id !== deleteRoleId));
      toast.success("Role deleted.");
      setIsDeleteModalOpen(false);
      setDeleteRoleId(null);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete role.");
    }
  };

  return (
    <main className="w-full min-w-0 bg-base-100">
      <section className="w-full border-b border-base-200 bg-base-100">
        <div className="border-b border-base-200 px-4 py-8 sm:px-8">
          <h1 className="ui-page-title tracking-tight">Roles</h1>
          <p className="ui-muted mt-2">
            Create, edit, and manage application roles.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 px-4 py-4 sm:px-8">
          <label className="input input-bordered relative flex min-h-11 min-w-0 flex-1 items-center gap-3 rounded-xl border-base-300 bg-white pl-4 pr-3 sm:max-w-xl">
            <FiSearch
              className="h-4 w-4 shrink-0 text-base-content/45"
              aria-hidden
            />
            <input
              type="search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              className="grow bg-transparent outline-none placeholder:text-base-content/40 ui-nav-text"
              placeholder="Search by role name..."
              autoComplete="off"
            />
          </label>
          <Can permission={PERMISSIONS.ROLE_CREATE}>
            <button type="button" className="ui-create-btn" onClick={openCreateModal}>
              <FiPlus className="ui-create-btn-icon" />
              Create role
            </button>
          </Can>
        </div>

        <div className="overflow-x-auto border-t border-base-200 px-4 sm:px-8">
          <table className="table table-zebra table-pin-rows table-md min-w-[760px] w-full">
            <thead className="border-b border-base-300 bg-base-200/70">
              <tr>
                <th scope="col" className="w-14 ui-table-header">
                  ID
                </th>
                <th scope="col" className="min-w-[14rem] ui-table-header">
                  ROLE NAME
                </th>
                <th scope="col" className="min-w-[10rem] ui-table-header">
                  SLUG
                </th>
                <th scope="col" className="min-w-[14rem] ui-table-header">
                  DESCRIPTION
                </th>
                <th scope="col" className="min-w-[8rem] ui-table-header whitespace-nowrap">
                  CREATED AT
                </th>
                <th scope="col" className="min-w-[8rem] ui-table-header whitespace-nowrap">
                  UPDATED AT
                </th>
                {showActions ? (
                  <th scope="col" className="ui-table-header ui-table-actions-col">
                    ACTIONS
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {isLoading
                ? Array.from({ length: 5 }).map((_, index) => (
                    <tr key={`skeleton-${index}`}>
                      {Array.from({ length: tableColCount }).map((__, innerIndex) => (
                        <td key={`cell-${innerIndex}`}>
                          <div className="skeleton my-2 h-4 w-full rounded-md" />
                        </td>
                      ))}
                    </tr>
                  ))
                : roles.map((role) => (
                    <tr key={role.id} className="transition-colors">
                      <td className="tabular-nums text-base-content/70 ui-nav-text">
                        {role.id}
                      </td>
                      <td className="ui-nav-text font-semibold text-base-content">
                        {role.name}
                      </td>
                      <td>
                        <span className="badge badge-ghost badge-sm font-mono text-xs">
                          {role.slug || "-"}
                        </span>
                      </td>
                      <td className="ui-nav-text text-base-content/85">
                        {role.description || "-"}
                      </td>
                      <td className="tabular-nums text-base-content/80 ui-nav-text">
                        {role.created_at}
                      </td>
                      <td className="tabular-nums text-base-content/80 ui-nav-text">
                        {role.updated_at}
                      </td>
                      {showActions ? (
                        <td className="ui-table-actions-col">
                          <TableRowActions
                            readPermission={PERMISSIONS.ROLE_READ}
                            updatePermission={PERMISSIONS.ROLE_UPDATE}
                            deletePermission={PERMISSIONS.ROLE_DELETE}
                            onView={() => openViewModal(role)}
                            onEdit={() => openEditModal(role)}
                            onDelete={() => askDeleteRole(role.id)}
                            viewLabel={`View ${role.name}`}
                            editLabel={`Edit ${role.name}`}
                            deleteLabel={`Delete ${role.name}`}
                          />
                        </td>
                      ) : null}
                    </tr>
                  ))}
              {!isLoading && roles.length === 0 ? (
                <tr>
                  <td colSpan={tableColCount} className="py-12 text-center">
                    <div className="alert inline-flex rounded-xl bg-base-200/70">
                      <span className="ui-nav-text">No roles found.</span>
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-base-200 px-4 py-4 sm:px-8">
          <p className="ui-meta">
            Showing {totalRecords === 0 ? 0 : (page - 1) * perPage + 1} to{" "}
            {Math.min(page * perPage, totalRecords)} of {totalRecords} entries
          </p>
          <div className="join">
            <button
              type="button"
              className="join-item btn btn-sm rounded-l-lg"
              disabled={page === 1}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            >
              Prev
            </button>
            {Array.from({ length: totalPages }).map((_, index) => {
              const current = index + 1;
              return (
                <button
                  key={current}
                  type="button"
                  className={`join-item btn btn-sm ${page === current ? "btn-active" : ""}`}
                  onClick={() => setPage(current)}
                >
                  {current}
                </button>
              );
            })}
            <button
              type="button"
              className="join-item btn btn-sm rounded-r-lg"
              disabled={page === totalPages}
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            >
              Next
            </button>
          </div>
        </div>
      </section>

      {isModalOpen ? (
        <dialog open className="modal modal-open">
          <div className="modal-box flex max-h-[92vh] w-[min(94vw,36rem)] max-w-xl flex-col overflow-hidden rounded-2xl bg-white p-0 sm:w-[min(94vw,40rem)] sm:max-w-2xl">
            <div className="border-b border-base-300 bg-white px-5 py-3">
              <h5 className="ui-panel-title">
                {editingRoleId ? "Update Role" : "Create Role"}
              </h5>
              <p className="ui-muted mt-2 text-sm">
                {editingRoleId
                  ? "Update role details and assigned permissions."
                  : "Add a new role and assign permissions."}
              </p>
            </div>

            <form
              className="flex min-h-0 flex-1 flex-col bg-white"
              onSubmit={handleSubmit}
            >
              <div className="ui-modal-scroll-body">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="form-control">
                    <label htmlFor="role-name" className="label">
                      <span className="label-text ui-label-form ui-nav-text text-sm font-semibold">
                        Name <span className="text-error">*</span>
                      </span>
                    </label>
                    <input
                      id="role-name"
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleFormChange}
                      className="input input-bordered input-sm h-9 min-h-9 rounded-lg bg-white text-xs sm:h-10 sm:text-sm"
                      required
                    />
                  </div>
                  <div className="form-control">
                    <label htmlFor="role-slug" className="label">
                      <span className="label-text ui-label-form ui-nav-text text-sm font-semibold">
                        Slug <span className="text-error">*</span>
                      </span>
                    </label>
                    <input
                      id="role-slug"
                      type="text"
                      name="slug"
                      value={form.slug}
                      onChange={handleFormChange}
                      className="input input-bordered input-sm h-9 min-h-9 rounded-lg bg-white text-xs sm:h-10 sm:text-sm"
                      required
                    />
                  </div>
                </div>

                <div className="form-control">
                  <label htmlFor="role-description" className="label">
                    <span className="label-text ui-label-form ui-nav-text text-sm font-semibold">
                      Description <span className="text-error">*</span>
                    </span>
                  </label>
                  <textarea
                    id="role-description"
                    name="description"
                    value={form.description}
                    onChange={handleFormChange}
                    className="textarea textarea-bordered textarea-sm min-h-[4.25rem] rounded-lg bg-white text-xs sm:text-sm"
                    rows={2}
                    required
                  />
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text text-sm font-semibold">
                      Permissions
                    </span>
                  </label>

                  <div className="max-h-[48vh] overflow-auto rounded-lg border border-base-300 p-3">
                    {isPermissionsLoading ? (
                      <p className="text-sm opacity-60">
                        Loading permissions...
                      </p>
                    ) : groupedPermissions.length === 0 ? (
                      <p className="text-sm opacity-60">No permissions found</p>
                    ) : (
                      <div className="space-y-3">
                        {groupedPermissions.map((group) => (
                          <div
                            key={group.key}
                            className="rounded-lg border border-base-200 p-3"
                          >
                            <p className="text-sm font-semibold text-base-content">
                              {group.label}
                            </p>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {group.permissions.map((permission) => {
                                const id = Number(permission.id);
                                const isSelected =
                                  selectedPermissionIds.includes(id);

                                return (
                                  <button
                                    key={id}
                                    type="button"
                                    onClick={() => togglePermission(id)}
                                    className={[
                                      "badge cursor-pointer px-2.5 py-1.5 text-[11px] transition-all duration-200 sm:text-xs",
                                      isSelected
                                        ? "badge-primary hover:brightness-95 hover:shadow-sm"
                                        : "badge-outline border-base-300 bg-white hover:border-primary/50 hover:bg-primary/10 hover:text-primary hover:shadow-sm",
                                    ].join(" ")}
                                    title={permission.name}
                                  >
                                    {permission.name}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 border-t border-base-200 bg-white px-5 py-3">
                <div className="modal-action mt-0">
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm h-9 min-h-9 rounded-lg px-4 text-sm"
                    onClick={resetModalState}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`btn btn-primary btn-sm h-9 min-h-9 rounded-lg px-4 text-sm ${isSubmitting ? "btn-disabled" : ""}`}
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? editingRoleId
                        ? "Updating..."
                        : "Creating..."
                      : editingRoleId
                        ? "Update Role"
                        : "Create Role"}
                  </button>
                </div>
              </div>
            </form>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button type="button" onClick={resetModalState}>
              close
            </button>
          </form>
        </dialog>
      ) : null}
      {viewingRole ? (
        <dialog open className="modal modal-open">
          <div className="ui-detail-modal-box">
            <div className="ui-detail-modal-header">
              <h3 className="ui-modal-title">{viewingRole.name}</h3>
              <p className="ui-muted mt-1">{viewingRole.slug || "Role"} · Permissions & access</p>
            </div>
            <div className="ui-detail-grid">
              <div className="ui-detail-card">
                <p className="ui-meta">ID</p>
                <p className="ui-detail-value-strong">
                  {viewingRole.id}
                </p>
              </div>
              <div className="ui-detail-card">
                <p className="ui-meta">Name</p>
                <p className="ui-detail-value-strong">
                  {viewingRole.name}
                </p>
              </div>
              <div className="ui-detail-card">
                <p className="ui-meta">Slug</p>
                <p className="ui-detail-value">
                  {viewingRole.slug || "-"}
                </p>
              </div>
              <div className="ui-detail-card">
                <p className="ui-meta">Description</p>
                <p className="ui-detail-value">
                  {viewingRole.description || "-"}
                </p>
              </div>
              <div className="ui-detail-card sm:col-span-2">
                <p className="ui-meta">Permissions</p>
                {(viewingRole.permissions || []).length ? (
                  <div className="mt-2 flex max-h-28 flex-wrap gap-2 overflow-auto pr-1">
                    {viewingRole.permissions
                      .map((permission) =>
                        typeof permission === "string"
                          ? permission
                          : permission?.name || permission?.slug,
                      )
                      .filter(Boolean)
                      .map((permissionName) => (
                        <span
                          key={permissionName}
                          className="badge badge-primary badge-sm border-primary/20 bg-primary/10 text-primary"
                        >
                          {permissionName}
                        </span>
                      ))}
                  </div>
                ) : (
                  <p className="ui-detail-value">-</p>
                )}
              </div>
              <div className="ui-detail-card">
                <p className="ui-meta">Created At</p>
                <p className="ui-detail-value">
                  {viewingRole.created_at}
                </p>
              </div>
              <div className="ui-detail-card">
                <p className="ui-meta">Updated At</p>
                <p className="ui-detail-value">
                  {viewingRole.updated_at}
                </p>
              </div>
            </div>
            <div className="modal-action mt-0 border-t border-base-200 px-6 py-4">
              <button
                type="button"
                className="btn btn-primary rounded-xl px-6 text-primary-content"
                onClick={() => setViewingRole(null)}
              >
                Close
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button type="button" onClick={() => setViewingRole(null)}>
              close
            </button>
          </form>
        </dialog>
      ) : null}
      {isDeleteModalOpen ? (
        <dialog open className="modal modal-open">
          <div className="modal-box max-w-md rounded-2xl bg-white p-0">
            <div className="border-b border-base-300 px-6 py-5">
              <h3 className="ui-modal-title">Delete Role</h3>
              <p className="ui-muted mt-2">
                Are you sure you want to delete this role? This action cannot be
                undone.
              </p>
            </div>
            <div className="modal-action mt-0 border-t border-base-200 px-6 py-4">
              <button
                type="button"
                className="btn btn-ghost rounded-xl"
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setDeleteRoleId(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-error rounded-xl"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button type="button" onClick={() => setIsDeleteModalOpen(false)}>
              close
            </button>
          </form>
        </dialog>
      ) : null}
    </main>
  );
}

export default RolesAccessPage;
